import { action$, ofType } from '@ardoq/rxbeach';
import { combineLatest, merge } from 'rxjs';
import { loadVisualizationSlideSuccess } from 'presentation/viewPane/actions';
import { debounceTime, map, shareReplay, startWith } from 'rxjs/operators';
import { resetGraph } from './graphModelActions';
import { notifyWorkspaceAggregatedLoaded } from 'streams/workspaces/actions';
import { startAction } from 'actions/utils';
import { referenceListToGraphData } from './util';
import { DEFAULT_DEBOUNCE_TIME } from './consts';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { loadedGraphOperations } from 'traversals/loadedGraphOperations';
import { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import { models$ } from 'streams/models/models$';
import {
  APIModelAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { getEmptyGraphModel, GraphModelShape } from '@ardoq/data-model';
import { getStreamCollectionInitialState } from 'streams/utils/streamUtils';
import { catchErrorLogAndContinue } from 'streams/utils/streamOperators';
import {
  references$,
  scenarioRelatedIds$,
} from 'streams/references/references$';
import {
  components$,
  emptyComponentsState,
  type ComponentsState,
} from 'streams/components/components$';
import { locale$ } from 'streams/locale$';
import { getDefaultLocale, type Locale } from '@ardoq/locale';

const toResetPayload = ({
  loadedGraph: { isViewpointMode, scopeReferenceIds },
  references,
  models,
  scenarioRelatedIds,
}: {
  loadedGraph: LoadedGraphWithViewpointMode;
  references: { models: APIReferenceAttributes[] };
  models: { models: APIModelAttributes[] };
  scenarioRelatedIds: Set<ArdoqId>;
}): [APIReferenceAttributes[], APIModelAttributes[]] => {
  const visibleReferences =
    scenarioRelatedIds.size > 0
      ? references.models.filter(ref => !scenarioRelatedIds.has(ref._id))
      : references.models;

  if (isViewpointMode) {
    const visibleReferenceIds = new Set(scopeReferenceIds);
    return [
      visibleReferences.filter(ref => visibleReferenceIds.has(ref._id)),
      models.models,
    ];
  }
  return [visibleReferences, models.models];
};

const toGraphModel = (input: {
  loadedGraph: LoadedGraphWithViewpointMode;
  references: { models: APIReferenceAttributes[] };
  models: { models: APIModelAttributes[] };
  scenarioRelatedIds: Set<ArdoqId>;
  components: ComponentsState;
  locale: Locale;
}): GraphModelShape => {
  const [refs, refModels] = toResetPayload(input);
  return {
    ...referenceListToGraphData(refs, refModels, input.locale),
    childrenByParent: input.components.childrenByParent,
    componentsById: input.components.byId,
  };
};

const graphModel$ = merge(
  combineLatest({
    resetAction: action$.pipe(
      ofType(notifyWorkspaceAggregatedLoaded, loadVisualizationSlideSuccess)
    ),
    loadedGraph: loadedGraph$,
    references: references$,
    models: models$,
    scenarioRelatedIds: scenarioRelatedIds$,
    components: components$,
    locale: locale$,
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    startWith({
      resetAction: startAction(),
      loadedGraph: loadedGraphOperations.getEmpty(),
      references: getStreamCollectionInitialState<APIReferenceAttributes>(),
      models: getStreamCollectionInitialState<APIModelAttributes>(),
      scenarioRelatedIds: new Set<ArdoqId>(),
      components: emptyComponentsState,
      locale: getDefaultLocale(),
    }),
    map(toGraphModel),
    catchErrorLogAndContinue(),
    shareReplay({ bufferSize: 1, refCount: true })
  ),
  action$.pipe(
    ofType(resetGraph),
    map((): GraphModelShape => getEmptyGraphModel())
  )
).pipe(
  catchErrorLogAndContinue(),
  shareReplay({ bufferSize: 1, refCount: true })
);

let currentGraphModel: GraphModelShape = getEmptyGraphModel();
let subscribed = false;

export const getCurrentGraphModel = (): GraphModelShape => {
  if (!subscribed) {
    subscribed = true;
    graphModel$.subscribe(model => {
      currentGraphModel = model;
    });
  }
  return currentGraphModel;
};

export { graphModel$ };
